import { useState } from "react";
import { education } from "../data/education";
import type { EducationItem } from "../types";
import { asset } from "../lib/asset";
import { useReveal } from "../hooks/useReveal";
import { useI18n } from "../i18n/LanguageContext";
import { Modal } from "./Modal";
import { Icon } from "./Icon";
import { SectionHead } from "./SectionHead";
import cards from "./cards.module.css";
import styles from "./Education.module.css";

function EducationCard({
  item,
  onOpen,
}: {
  item: EducationItem;
  onOpen: (item: EducationItem) => void;
}) {
  const { t, ui } = useI18n();
  const reveal = useReveal();
  const hasModal = Boolean(item.certificates?.length);

  const inner = (
    <>
      <div className={cards.media}>
        {item.period && (
          <div className={cards.badges}>
            <span className={`${cards.badge} ${cards.badgeAccent}`}>
              {item.period}
            </span>
          </div>
        )}
        <img src={asset(item.image)} alt={t(item.title)} loading="lazy" />
      </div>
      <div className={cards.body}>
        <h3 className={cards.title}>{t(item.title)}</h3>
        <p className={cards.summary}>{t(item.summary)}</p>
        <span className={cards.more}>
          {hasModal ? ui.education.viewCertificates : ui.education.visit}
          <Icon name="external" size={15} />
        </span>
      </div>
    </>
  );

  if (hasModal) {
    return (
      <div
        className={cards.card}
        ref={reveal}
        role="button"
        tabIndex={0}
        onClick={() => onOpen(item)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(item);
          }
        }}
      >
        {inner}
      </div>
    );
  }

  return (
    <a
      className={cards.card}
      ref={reveal}
      href={item.link?.url}
      target="_blank"
      rel="noreferrer"
    >
      {inner}
    </a>
  );
}

export function Education() {
  const { t, ui } = useI18n();
  const [active, setActive] = useState<EducationItem | null>(null);

  return (
    <section id="education" className={`section band ${styles.section}`}>
      <div className="container">
        <SectionHead
          eyebrow={ui.education.eyebrow}
          title={ui.education.title}
          sub={ui.education.sub}
        />

        <div className={cards.grid}>
          {education.map((item) => (
            <EducationCard key={item.id} item={item} onOpen={setActive} />
          ))}
        </div>
      </div>

      {active && (
        <Modal title={t(active.title)} onClose={() => setActive(null)}>
          {active.note && <p className={styles.note}>{t(active.note)}</p>}
          {active.certificates && (
            <ul className={styles.certs}>
              {active.certificates.map((cert) => (
                <li key={cert.url}>
                  <a href={cert.url} target="_blank" rel="noreferrer">
                    <span>{cert.label}</span>
                    <Icon name="external" size={16} />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </section>
  );
}
